import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { getTvListCall, getCurrentTokenClaims } from "../../../api/Tv";

const getPosterUrl = (posterPath) => {
    if (!posterPath) return "";
    if (posterPath.startsWith("http://") || posterPath.startsWith("https://") || posterPath.startsWith("data:image/")) {
        return posterPath;
    }
    return `https://public.nanbateam.com/${posterPath.replace(/^\/+/, '').replace(/^images\//, '')}`;
};

const SideBar = () => {
    const location = useLocation();
    const currentPath = location.pathname;
    const [channelCount, setChannelCount] = useState(null);

    const tokenClaims = getCurrentTokenClaims();
    const isClient = tokenClaims?.role === 'client' || Boolean(tokenClaims?.tv_id);
    const clientTvId = tokenClaims?.tv_id;

    let userInfo = {};
    try {
        userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
    } catch (e) {}

    const [stationLogo, setStationLogo] = useState(userInfo?.station?.logo || "");
    const [stationName, setStationName] = useState(userInfo?.station?.name || (isClient ? "My TV Station" : "NanbaTeam"));
    const [stationCode, setStationCode] = useState(userInfo?.station?.code || (isClient ? "station.tv" : "nanbateam.com"));

    const loadStationData = useCallback(() => {
        getTvListCall()
            .then(res => {
                const list = res?.data?.data?.data || res?.data?.data || [];
                if (Array.isArray(list)) {
                    if (!isClient) setChannelCount(list.length);
                    if (list.length > 0) {
                        const targetStation = (clientTvId ? list.find(s => String(s.id) === String(clientTvId)) : null) || list[0];
                        if (targetStation) {
                            if (targetStation.logo) setStationLogo(targetStation.logo);
                            if (targetStation.name) setStationName(targetStation.name);
                            if (targetStation.code) setStationCode(targetStation.code);
                        }
                    }
                }
            })
            .catch(() => {});
    }, [isClient, clientTvId]);

    useEffect(() => {
        loadStationData();

        const handleLogoUpdate = (e) => {
            if (e.detail?.logo) {
                setStationLogo(e.detail.logo);
            }
            if (e.detail?.name) {
                setStationName(e.detail.name);
            }
        };

        window.addEventListener("stationLogoUpdated", handleLogoUpdate);
        return () => window.removeEventListener("stationLogoUpdated", handleLogoUpdate);
    }, [currentPath, loadStationData]);

    const getInitials = (name) => {
        if (!name) return isClient ? "SO" : "AD";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    return (
        <aside className="sidebar">
            {/* Brand Header */}
            <div className="sidebar-header">
                <div
                    className={`brand-icon-box ${isClient ? "brand-icon-client" : ""}`}
                    style={{
                        background: stationLogo ? "#030712" : (isClient ? "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)" : undefined),
                        border: stationLogo ? "1px solid var(--border-subtle)" : undefined,
                        overflow: "hidden",
                        padding: stationLogo ? "3px" : undefined
                    }}
                >
                    {stationLogo ? (
                        <img
                            src={getPosterUrl(stationLogo)}
                            alt={stationName}
                            style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "8px" }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : isClient ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                            <polyline points="17 2 12 7 7 2" />
                            <line x1="8" y1="14" x2="8.01" y2="14" strokeWidth="3" />
                            <line x1="12" y1="14" x2="16" y2="14" />
                        </svg>
                    ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                            <polyline points="2 17 12 22 22 17"></polyline>
                            <polyline points="2 12 12 17 22 12"></polyline>
                        </svg>
                    )}
                </div>
                <div className="brand-info">
                    <h2>{stationName}</h2>
                    <span>{stationCode}</span>
                </div>
            </div>

            {/* Navigation */}
            <div className="sidebar-nav">
                {isClient ? (
                    /* CLIENT / STATION OWNER NAVIGATION */
                    <div>
                        <div className="nav-section-title">STATION CONTROL</div>
                        <ul className="nav-list">
                            <li>
                                <Link to="/station" className={`nav-item-link ${currentPath === "/station" ? "active" : ""}`}>
                                    <span className="nav-item-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                            <polyline points="21 15 16 10 5 21"></polyline>
                                        </svg>
                                    </span>
                                    <span>Station Studio & Banners</span>
                                </Link>
                            </li>
                        </ul>

                        <div className="nav-section-title" style={{ marginTop: "24px" }}>QUICK ACCESS</div>
                        <ul className="nav-list">
                            <li>
                                <Link
                                    to="/station?tab=banners"
                                    onClick={() => {
                                        const event = new CustomEvent("switchStudioTab", { detail: "banners" });
                                        window.dispatchEvent(event);
                                    }}
                                    className="nav-item-link"
                                >
                                    <span className="nav-item-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                            <line x1="8" y1="21" x2="16" y2="21"></line>
                                            <line x1="12" y1="17" x2="12" y2="21"></line>
                                        </svg>
                                    </span>
                                    <span>Banner Ads & Posters</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/station?tab=stream"
                                    onClick={() => {
                                        const event = new CustomEvent("switchStudioTab", { detail: "stream" });
                                        window.dispatchEvent(event);
                                    }}
                                    className="nav-item-link"
                                >
                                    <span className="nav-item-icon">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                        </svg>
                                    </span>
                                    <span>Live Stream & Profile</span>
                                </Link>
                            </li>
                        </ul>
                    </div>
                ) : (
                    /* SUPER ADMIN NAVIGATION */
                    <>
                        <div>
                            <div className="nav-section-title">BROADCAST PLATFORM</div>
                            <ul className="nav-list">
                                <li>
                                    <Link to="/dashboard" className={`nav-item-link ${currentPath === "/dashboard" ? "active" : ""}`}>
                                        <span className="nav-item-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="3" width="7" height="7"></rect>
                                                <rect x="14" y="14" width="7" height="7"></rect>
                                                <rect x="3" y="14" width="7" height="7"></rect>
                                            </svg>
                                        </span>
                                        <span>Overview</span>
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/tv" className={`nav-item-link ${currentPath === "/tv" ? "active" : ""}`}>
                                        <span className="nav-item-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                                                <polyline points="17 2 12 7 7 2" />
                                            </svg>
                                        </span>
                                        <span>All TV Channels</span>
                                        {channelCount !== null && (
                                            <span className="nav-badge">{channelCount}</span>
                                        )}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <div className="nav-section-title">CLIENT ACCESS PORTAL</div>
                            <ul className="nav-list">
                                <li>
                                    <Link to="/station" className={`nav-item-link ${currentPath === "/station" ? "active" : ""}`}>
                                        <span className="nav-item-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                <polyline points="21 15 16 10 5 21"></polyline>
                                            </svg>
                                        </span>
                                        <span>Station Studio & Banners</span>
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <div className="nav-section-title">INFRASTRUCTURE</div>
                            <ul className="nav-list">
                                <li>
                                    <a
                                        href="#gateway"
                                        onClick={(e) => { e.preventDefault(); if (currentPath !== "/dashboard") window.location.href = "/dashboard#gateway"; }}
                                        className="nav-item-link"
                                    >
                                        <span className="nav-item-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                                            </svg>
                                        </span>
                                        <span>API Gateway</span>
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#services"
                                        onClick={(e) => { e.preventDefault(); if (currentPath !== "/dashboard") window.location.href = "/dashboard#services"; }}
                                        className="nav-item-link"
                                    >
                                        <span className="nav-item-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                                            </svg>
                                        </span>
                                        <span>Microservices (7)</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </>
                )}
            </div>

            {/* Profile & Logout */}
            <div className="sidebar-footer">
                <div className="user-profile-card">
                    <div
                        className="user-avatar"
                        style={{
                            background: stationLogo ? "#030712" : (isClient ? "linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)" : undefined),
                            border: stationLogo ? "1px solid var(--border-subtle)" : undefined,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: stationLogo ? "2px" : undefined,
                            color: "#fff",
                            flexShrink: 0
                        }}
                    >
                        {stationLogo ? (
                            <img
                                src={getPosterUrl(stationLogo)}
                                alt={stationName}
                                style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "6px" }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            getInitials(userInfo?.name || stationName)
                        )}
                    </div>
                    <div className="user-meta">
                        <div className="user-name">
                            {stationName || userInfo?.name || (isClient ? "Station Owner" : "Nanba Admin")}
                        </div>
                        <div className="user-role">
                            <span className="user-role-dot" style={isClient ? { background: "#06b6d4", boxShadow: "0 0 8px #06b6d4" } : {}}></span>
                            {isClient
                                ? `Online (${stationName})`
                                : "Online (Super Admin)"}
                        </div>
                    </div>
                    <Link to="/logout" className="logout-icon-btn" title="Sign Out">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                    </Link>
                </div>
            </div>
        </aside>
    );
};

export default SideBar;
